// 자산 URL 해석의 단일 창구(자산 패치 구조). 빌드가 public/assets/** 를 내용 해시 파일명으로 복사해 두고
// (assets/h/<폴더>/<이름>.<해시>.<확장자>) index.html 에 "원래 경로 → 해시:바이트" 표를 넣는다. 코드는 원래 경로 그대로 요청하고,
// 이 모듈이 표에 있으면 해시 경로로 바꿔 준다. 표에 없거나(개발 서버·새 파일) 표가 없으면 원래 경로 그대로다 — 옛 경로는 서버에 그대로 있어 롤백·이전 클라이언트에 안전하다.
// 순수 모듈: DOM·저장소를 만지지 않는다(서비스 워커도 같은 규칙을 쓴다).

/** index.html 에 인라인으로 들어가는 표: f[원래 경로(assets/ 아래)] = "<해시>:<바이트>" */
export interface AssetManifestData {
  /** 빌드 버전(표 + 번들 이름의 해시). 서비스 워커가 새 버전을 알아보는 기준 */
  v: string;
  f: Record<string, string>;
}

export interface AssetEntry { rel: string; hash: string; bytes: number }

/** 해시 파일이 놓이는 폴더(assets/ 아래) */
export const HASHED_DIR = 'h/';
/** 해시 길이(16진 문자) */
export const HASH_LEN = 10;

/** 원래 경로 → 해시 경로. 'r1/foo.glb' + 'abc' → 'h/r1/foo.abc.glb'. 확장자가 없으면 끝에 붙인다 */
export function hashedRel(rel: string, hash: string): string {
  const slash = rel.lastIndexOf('/');
  const dir = rel.slice(0, slash + 1), name = rel.slice(slash + 1);
  const dot = name.lastIndexOf('.');
  return HASHED_DIR + dir + (dot > 0 ? name.slice(0, dot) + '.' + hash + name.slice(dot) : name + '.' + hash);
}

const HASHED_RE = new RegExp('^' + HASHED_DIR + '(.*?)([^/]+?)\\.([0-9a-f]{' + HASH_LEN + '})(\\.[^./]+)?$');
/** 해시 경로(assets/ 아래) → 원래 경로. 해시 경로가 아니면 null. 서비스 워커의 폴백(해시 파일이 서버에서 사라진 옛 클라이언트)이 쓴다 */
export function originalRel(rel: string): string | null {
  const m = HASHED_RE.exec(rel);
  return m ? m[1] + m[2] + (m[4] ?? '') : null;
}

export function encodeEntry(hash: string, bytes: number): string { return hash + ':' + bytes; }
export function decodeEntry(v: string): { hash: string; bytes: number } | null {
  const i = v.indexOf(':');
  if (i <= 0) return null;
  const bytes = Number(v.slice(i + 1));
  return Number.isFinite(bytes) ? { hash: v.slice(0, i), bytes } : null;
}

/** 표 → 항목 목록 */
export function entriesOf(data: AssetManifestData | null | undefined): AssetEntry[] {
  const out: AssetEntry[] = [];
  if (!data || !data.f) return out;
  for (const rel of Object.keys(data.f)) {
    const d = decodeEntry(data.f[rel]);
    if (d) out.push({ rel, hash: d.hash, bytes: d.bytes });
  }
  return out;
}

let current: Map<string, AssetEntry> | null = null;
let currentVersion = '';

/** 표를 설치한다(부팅 때 index.html 의 인라인 표, 시험에서는 직접). null 이면 표 없음 = 원래 경로 그대로 */
export function installAssetManifest(data: AssetManifestData | null | undefined): void {
  if (!data || !data.f) { current = null; currentVersion = ''; return; }
  current = new Map(entriesOf(data).map((e) => [e.rel, e]));
  currentVersion = String(data.v ?? '');
}

/** index.html 이 넣어 둔 표를 읽어 설치한다(한 번) */
export function installFromWindow(w: { __DH_ASSETS__?: AssetManifestData } | undefined = typeof window !== 'undefined' ? (window as unknown as { __DH_ASSETS__?: AssetManifestData }) : undefined): void {
  if (w && w.__DH_ASSETS__) installAssetManifest(w.__DH_ASSETS__);
}

export function manifestVersion(): string { return currentVersion; }
export function manifestEntries(): AssetEntry[] { return current ? [...current.values()] : []; }
export function manifestEntry(rel: string): AssetEntry | undefined { return current?.get(rel); }
export function hasManifest(): boolean { return current !== null && current.size > 0; }

/** 앞쪽 'assets/' 앞에 올 수 있는 것: 없음, './', '/', 'https://호스트/' 또는 'https://호스트/./' */
const URL_RE = /^((?:https?:\/\/[^/]+)?(?:\.\/|\/)?)assets\/([^?#]*)(.*)$/;

/**
 * 자산 주소 문자열을 해시 경로로 바꾼다. 'assets/…' 꼴이 아니거나 표에 없으면 그대로 돌려준다(검색·해시 조각은 유지).
 * 상대('assets/…', './assets/…')·절대('/assets/…')·같은 출처 전체 주소 모두 앞부분을 보존한다.
 */
export function resolveAssetUrl(url: string): string {
  if (!current || current.size === 0) return url;
  const m = URL_RE.exec(url);
  if (!m) return url;
  const e = current.get(m[2]);
  return e ? m[1] + 'assets/' + hashedRel(m[2], e.hash) + m[3] : url;
}

/** assets/ 아래 상대 경로('r1/kit_wall.glb')로 최종 주소를 만든다(BASE_URL 기준) */
export function assetUrl(rel: string): string {
  return resolveAssetUrl((import.meta.env.BASE_URL ?? './') + 'assets/' + rel);
}

/** URL(경로)에서 assets/ 아래 상대 경로를 뽑는다. assets/ 가 아니면 null (서비스 워커 라우팅·시험용) */
export function relOfPath(pathname: string): string | null {
  const i = pathname.indexOf('/assets/');
  return i >= 0 ? pathname.slice(i + '/assets/'.length) : null;
}

// 모듈이 처음 읽힐 때 index.html 의 인라인 표를 설치한다(이 모듈을 import 하는 다른 모듈의 상수보다 먼저 평가된다)
installFromWindow();
